<template>
  <div>
    <!-- DESKTOP: right side panel -->
    <b-sidebar
      v-if="!isPhone"
      :visible="!!node"
      right
      shadow
      backdrop
      width="400px"
      :title="title"
      sidebar-class="rx-vh-sidebar"
      @hidden="close">
      <div v-if="node" class="rx-vh">
        <div class="rx-vh__file" :title="node.name">{{ node.name }}</div>
        <ul class="rx-vh__list">
          <li v-for="v in versions" :key="v.number" class="rx-vh__row">
            <div class="rx-vh__body">
              <div class="rx-vh__title">Version {{ v.number }} <span v-if="v.isCurrent" class="rx-vh__tag">Current</span></div>
              <div class="rx-vh__meta">{{ metaOf(v) }}</div>
            </div>
            <div class="rx-vh__actions">
              <button type="button" class="rx-vh__btn" :disabled="busy" :aria-label="'Download version ' + v.number" @click="download(v)">
                <i :class="downloading === v.number ? 'fas fa-spinner fa-spin' : 'fas fa-download'"></i> Download
              </button>
              <button v-if="!v.isCurrent" type="button" class="rx-vh__btn" :disabled="busy" :aria-label="'Restore version ' + v.number" @click="askRestore(v)">
                <i class="fas fa-undo"></i> Restore
              </button>
            </div>
          </li>
        </ul>
        <p v-if="versions.length <= 1" class="rx-vh__hint">Upload a file with the same name to add a version.</p>
      </div>
    </b-sidebar>

    <!-- PHONE: full-screen sheet -->
    <div v-else-if="node" class="rx-vh-phone" role="dialog" aria-modal="true" :aria-label="title">
      <header class="rx-vh-phone__head">
        <button type="button" class="rx-vh-phone__back" aria-label="Close version history" @click="close"><i class="fas fa-arrow-left"></i></button>
        <div class="rx-vh-phone__titles">
          <div class="rx-vh-phone__title">{{ title }}</div>
          <div class="rx-vh-phone__sub">{{ node.name }}</div>
        </div>
      </header>
      <div class="rx-vh rx-vh--phone">
        <ul class="rx-vh__list">
          <li v-for="v in versions" :key="v.number" class="rx-vh__row rx-vh__row--phone">
            <div class="rx-vh__body">
              <div class="rx-vh__title">Version {{ v.number }} <span v-if="v.isCurrent" class="rx-vh__tag">Current</span></div>
              <div class="rx-vh__meta">{{ metaOf(v) }}</div>
            </div>
            <div class="rx-vh__actions">
              <button type="button" class="rx-vh__icon" :disabled="busy" :aria-label="'Download version ' + v.number" @click="download(v)">
                <i :class="downloading === v.number ? 'fas fa-spinner fa-spin' : 'fas fa-download'"></i>
              </button>
              <button v-if="!v.isCurrent" type="button" class="rx-vh__icon" :disabled="busy" :aria-label="'Restore version ' + v.number" @click="askRestore(v)">
                <i class="fas fa-undo"></i>
              </button>
            </div>
          </li>
        </ul>
        <p v-if="versions.length <= 1" class="rx-vh__hint">Upload a file with the same name to add a version.</p>
      </div>
    </div>

    <!-- Restore confirmation -->
    <b-modal
      :visible="!!restoreTarget"
      title="Restore version"
      hide-footer
      centered
      :no-close-on-backdrop="restoring"
      :no-close-on-esc="restoring"
      @hide="onRestoreHide">
      <div v-if="restoreTarget">
        <p class="rx-vh__confirm">Restore version {{ restoreTarget.number }} as a new current version?</p>
        <div class="rx-vh__confirm-foot">
          <button type="button" class="rx-vh__btn" :disabled="restoring" @click="restoreTarget = null">Cancel</button>
          <button type="button" class="rx-vh__btn rx-vh__btn--primary" :disabled="restoring" @click="restore">{{ restoring ? 'Restoring…' : 'Restore' }}</button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { formatDate, personName, fileApiId, filePropertyId } from './resourcesData'
import { sortedVersions, formatSize } from './fileVersions'

export default {
  name: 'VersionHistory',
  props: {
    // File node (resourcesData normalizeFile) or null when closed
    node: { type: Object, default: null },
    isPhone: { type: Boolean, default: false }
  },
  data () {
    return {
      downloading: null,
      restoreTarget: null,
      restoring: false
    }
  },
  computed: {
    title () {
      return 'Version history'
    },
    raw () {
      return (this.node && this.node.raw) || {}
    },
    versions () {
      return sortedVersions(this.raw)
    },
    busy () {
      return this.downloading !== null || this.restoring
    }
  },
  watch: {
    node: function (n, old) {
      if (!n || !old || n.id !== old.id) this.restoreTarget = null
    }
  },
  methods: {
    close () {
      if (this.restoring) return
      this.$emit('close')
    },
    // Uploader only when it can be matched to this version's person id
    uploaderOf (v) {
      var f = this.raw
      var p = f.uploadedByPerson
      if (!p || v.uploadedByPersonId === null || v.uploadedByPersonId === undefined) return ''
      var pid = typeof p === 'object' ? (p.id !== undefined ? p.id : p.personId) : f.uploadedByPersonId
      if (pid === undefined || pid === null || String(pid) !== String(v.uploadedByPersonId)) return ''
      return personName(p) || ''
    },
    metaOf (v) {
      var parts = []
      var date = formatDate(v.dateUploaded, 'long')
      if (date) parts.push(date)
      var who = this.uploaderOf(v)
      if (who) parts.push(who)
      if (v.sizeBytes !== null && v.sizeBytes !== undefined) parts.push(formatSize(v.sizeBytes))
      return parts.join(' · ')
    },
    download (v) {
      var self = this
      if (this.busy || !this.node) return
      this.downloading = v.number
      this.$store.dispatch('resources/downloadFileVersion', {
        propertyId: filePropertyId(this.node),
        fileId: fileApiId(this.node),
        number: v.number,
        fileName: v.fileName || this.raw.fileName || this.node.name
      }).then(function () {
        self.downloading = null
      }, function () {
        self.downloading = null
        self.$toasted.error('Version ' + v.number + ' could not be downloaded. Please try again.', { duration: 5000 })
      })
    },
    askRestore (v) {
      if (this.busy) return
      this.restoreTarget = v
    },
    onRestoreHide (evt) {
      if (this.restoring) {
        evt.preventDefault()
        return
      }
      this.restoreTarget = null
    },
    restore () {
      var self = this
      var v = this.restoreTarget
      if (!v || this.restoring || !this.node) return
      this.restoring = true
      this.$store.dispatch('resources/restoreFileVersion', {
        propertyId: filePropertyId(this.node),
        fileId: fileApiId(this.node),
        number: v.number
      }).then(function (r) {
        self.restoring = false
        self.restoreTarget = null
        var newNumber = r && r.versionNumber
        self.$toasted.success('Restored version ' + v.number + (newNumber ? ' as version ' + newNumber : ''), { duration: 3000 })
        self.$emit('restored', r && r.file ? r.file : null)
      }, function () {
        self.restoring = false
        self.$toasted.error('Version ' + v.number + ' could not be restored. Please try again.', { duration: 5000 })
      })
    }
  }
}
</script>

<style scoped>
.rx-vh {
  padding: 0 16px 16px;
  color: #1f2937;
}
.rx-vh__file {
  margin-bottom: 8px;
  font-size: 13px;
  color: #6b7280;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.rx-vh__list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.rx-vh__row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  padding: 12px 0;
  border-bottom: 1px solid #eef0f2;
}
.rx-vh__body {
  flex: 1 1 auto;
  min-width: 0;
}
.rx-vh__title {
  font-weight: 600;
  font-size: 14px;
}
.rx-vh__tag {
  display: inline-block;
  margin-left: 6px;
  padding: 0 8px;
  border-radius: 999px;
  background: #e3f1ef;
  color: #1a7f86;
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
}
.rx-vh__meta {
  font-size: 12px;
  color: #6b7280;
}
.rx-vh__actions {
  display: flex;
  flex: 0 0 auto;
}
.rx-vh__btn {
  height: 36px;
  margin-left: 6px;
  padding: 0 10px;
  border: 1px solid #e2e5ea;
  border-radius: 6px;
  background: #fff;
  color: #1f2937;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}
.rx-vh__btn--primary {
  background: #1a7f86;
  border-color: #1a7f86;
  color: #fff;
}
.rx-vh__btn:disabled,
.rx-vh__icon:disabled {
  opacity: 0.6;
}
.rx-vh__icon {
  width: 44px;
  height: 44px;
  margin-left: 4px;
  border: 1px solid #e2e5ea;
  border-radius: 8px;
  background: #fff;
  color: #4b5563;
}
.rx-vh__hint {
  margin: 12px 0 0;
  font-size: 13px;
  color: #6b7280;
}
.rx-vh__confirm {
  margin: 0 0 16px;
}
.rx-vh__confirm-foot {
  display: flex;
  justify-content: flex-end;
}
.rx-vh-phone {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1040;
  background: #fff;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
.rx-vh-phone__head {
  position: sticky;
  top: 0;
  display: flex;
  align-items: center;
  min-height: 56px;
  padding: 0 8px;
  border-bottom: 1px solid #e2e5ea;
  background: #fff;
}
.rx-vh-phone__back {
  width: 44px;
  height: 44px;
  border: 0;
  background: transparent;
  color: #1f2937;
  font-size: 18px;
}
.rx-vh-phone__titles {
  min-width: 0;
  margin-left: 4px;
}
.rx-vh-phone__title {
  font-weight: 700;
  font-size: 17px;
}
.rx-vh-phone__sub {
  font-size: 13px;
  color: #6b7280;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.rx-vh--phone {
  padding-top: 4px;
}
.rx-vh__row--phone {
  min-height: 64px;
}
</style>
